import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { facebookLib } from '@lib/facebook'
import { Booking } from 'types/model/booking'
import { analytics } from '@lib/analytics'
import { PurchaseTrackingData } from 'types/lib/analytics'
import { TrackEvent } from '@utils/analytics/types'
import { TrackingEvent } from 'shared'
import { trackEvent } from '@lib/track'
import {
  getLuxonDateFormatByVenueDateDisplayFormat,
  utcToISO,
  withDateFormat,
} from '@utils/date-time'

export function usePageview() {
  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = () => {
      facebookLib.trackPageview({ url: window.location.href })
    }
    router.events.on('routeChangeComplete', handleRouteChange)

    return () => router.events.off('routeChangeComplete', handleRouteChange)
  }, [router.events])

  useEffect(() => {
    facebookLib.trackPageview({ url: window.location.href })
  }, [])
}

export function usePurchaseConfirmation(booking: Booking) {
  const hasFiredFacebookPurchase = useRef(false)
  const hasTrackedCompletedOrder = useRef(false)
  const dateFormat = getLuxonDateFormatByVenueDateDisplayFormat(
    booking.venue.dateDisplayFormat
  )

  useEffect(() => {
    if (
      hasFiredFacebookPurchase.current ||
      Date.now() - booking.modified > 30_000
    ) {
      return
    }

    hasFiredFacebookPurchase.current = true

    const purchaseData: PurchaseTrackingData = {
      name: booking.name,
      firstName: booking.primaryUser.firstName as string,
      lastName: booking.primaryUser.lastName as string,
      email: booking.primaryUser.email as string,
      userId: booking.primaryUser.id,
      bookingId: booking.id,
      venueId: booking.venue.id,
      eventId: booking.event,
      currency: booking.venue.currencyCode,
      value: (booking.total ?? 0).toString(),
    }

    analytics.trackPurchase(purchaseData)

    analytics.trackEvent({
      event: TrackEvent.CompletedPurchase,
      venue: booking.venue,
      eventData: {
        date: utcToISO(booking.date),
        dateFormatted: withDateFormat(booking.date, dateFormat, 'UTC'),
        arrivalTime: booking.arrivalTime
          ? withDateFormat(
              booking.arrivalTime,
              'h:mm a',
              booking.venue.timezone || booking.venue.location?.timezone
            )
          : undefined,
        dueOnArrival: booking.dueOnArrival,
        bookingId: booking.id,
        firstName: booking.primaryUser.firstName,
        lastName: booking.primaryUser.lastName,
        email: booking.primaryUser.email,
        userId: booking.primaryUser.id,
        phone: booking.primaryUser.phone,
        items: [
          {
            itemName: booking.location,
            itemId: booking.id,
            itemPrice: booking.price,
            itemCategory: booking.inventoryType,
            itemQuantity: 1,
          },
        ],
        currency: booking.venue.currencyCode,
        total: booking.total,
        tax: booking.tax,
        purchaseId: booking.id,
        // promoCode: booking.promoCode,
      },
    })
  }, [booking, dateFormat])

  useEffect(() => {
    if (hasTrackedCompletedOrder.current) return
    hasTrackedCompletedOrder.current = true

    void trackEvent({
      event: TrackingEvent.CompletedOrder,
      venueId: booking.venue.id,
      eventId: booking.event,
      orderId: booking.id,
    })
  }, [booking.event, booking.id, booking.venue.id])
}
